/* PreNumbers PSBB — Export Data (reskinned).

   The data-merge logic (mergeData, formatMetric, template CRUD) is unchanged —
   only the markup is reskinned to the .pn-* design system. The Template Maker
   itself is already modernized and is rendered as-is. */

import { useState, useContext, useEffect, useCallback } from 'react';

import { PSSBPageContext } from '../PSBBPage.jsx';
import { genericGETDatabaseCall, genericPOSTDatabaseCall } from '../../helperFunctions.jsx';
import TemplateMaker from '../../TemplateMaker/TemplateMaker.jsx';
import { currencySymbols } from "../../constants";
import { PageHead, FbIcons } from '../../FinancialBuilder/fbUI.jsx';
import { ArrowRightOutlined, LayoutOutlined } from '@ant-design/icons';

const { ArrowLeftOutlined, PlusOutlined, SaveOutlined, SlidersOutlined } = FbIcons;

async function getAllTemplates() {
    return await genericGETDatabaseCall('/api/psbb/getAllTemplates?type=psbb', "all templates");
}

async function addTemplateToDatabase(templateData) {
    return genericPOSTDatabaseCall('/api/psbb/addTemplate', templateData, 'template');
}

async function saveTemplateToDatabase(templateData) {
    return genericPOSTDatabaseCall('/api/psbb/saveTemplate', { templateData }, 'template');
}

function formatMetric(metric, value, currency) {
    if (Array.isArray(value) && metric.data_type === "Currency") {
        return value.map((val, indx) => indx !== 0 ? currencySymbols[currency] + val : val);
    }

    if (metric.data_type === "Currency") {
        return currencySymbols[currency] + value;
    }
    return value;
}

function ExportDataPage() {
    const { goTo, dataForExport, messageApi, entitySelected, mergedData, setMergedData } = useContext(PSSBPageContext);
    const [openTemplateMaker, setOpenTemplateMaker] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [currentTemplate, setCurrentTemplate] = useState({
        name: "",
        metrics: [],
        id: ""
    });
    const [templateSelected, setTemplateSelected] = useState(false);
    const [newTemplateName, setNewTemplateName] = useState("");

    const handleAddTemplate = async () => {
        messageApi.info("Adding template...");
        try {
            const resp = await addTemplateToDatabase({ name: newTemplateName, entity_type: 'psbb' });
            messageApi.success("Template added successfully.");
            setTemplates((prevTemplates) => [...prevTemplates, resp.template]);
        } catch (error) {
            messageApi.error("Error adding template.");
            console.error("Error adding template:", error);
        }
    };

    const handleSaveTemplate = async () => {
        messageApi.info("Saving template...");
        console.log(currentTemplate);
        try {
            await saveTemplateToDatabase(currentTemplate);
            messageApi.destroy();
            messageApi.success("Template saved successfully.");
        } catch (error) {
            messageApi.destroy();
            messageApi.error("Error saving template.");
            console.error("Error saving template:", error);
        }
    }

    const handleGetAllTemplates = useCallback(async () => {
        messageApi.info("Getting all templates...");
        try {
            const resp = await getAllTemplates();
            setTemplates(resp.templates);
            messageApi.destroy();
            messageApi.success("Templates retrieved successfully.");
        } catch (error) {
            messageApi.destroy();
            messageApi.error("Error getting templates.");
            console.error("Error getting templates:", error);
        }
    }, [messageApi]);

    useEffect(() => {
        handleGetAllTemplates();
    }, [handleGetAllTemplates]);

    useEffect(() => {
        console.log(mergedData);
    }, [mergedData]);

    const mergeData = () => {
        const flattenedData = Object.entries(dataForExport).flat().flat().flat(); // Needed so we can we easily find projections
        const perProductMapping = {
            "Revenue by Product (per product)": ["formattedData", 4], // 4th index has revenue by product
            "Revenue Growth % (per product)": ["percentage_data", 1], //  1st index has revenue growth %
            "Product Units/Subscr. (per product)": ["formattedData", 5], // 5th index has product units/subscriptions
            "Sales Targets Units / New Subscr. (per product)": ["formattedData", 6], // 6th index has sales target units / new subscr.
            "Churn Subscr. (per product)": ["formattedData", 7], // 7th index has churn subscr.
        };

        const mergedData = [];

        for (let metric of currentTemplate.metrics) {
            if (metric.type === "Big Number Card (KPIs)") {
                mergedData.push({
                    id: "widget_" + crypto.randomUUID(), data_type: 'Big Number Card', data: formatMetric(metric, dataForExport.allPSBBFirmWideMapping[metric.name], dataForExport.currency), name: metric.user_given_name
                });
                continue;
            }

            if (metric.type === "Table (Firm Wide) KPIs") {
                console.log("Data for Table (Firm Wide) KPIs:");
                let tableData = [
                    [], // Headers
                    [] // Values
                ];
                for (let tableMetric of metric.table_metrics) {
                    tableData[0].push(tableMetric.name)
                    tableData[1].push(formatMetric(tableMetric, dataForExport.allPSBBFirmWideMapping[tableMetric.name], dataForExport.currency));
                }
                mergedData.push({ id: "widget_" + crypto.randomUUID(), data_type: 'Two Col Table', data: tableData, name: metric.user_given_name });
                continue;
            }

            if (metric.type === "Table (Projections)" || metric.type === "Chart (Projections)") {
                console.log(`Data for ${metric.type}:`);
                let tableData = [];
                for (let tableMetric of metric.table_metrics) {
                    if (tableMetric.name.includes("per product")) {
                        if (perProductMapping[tableMetric.name] === undefined) {
                            console.warn("No mapping found for per product metric:", tableMetric.name);
                            continue;
                        }

                        const [dataKey, dataIndex] = perProductMapping[tableMetric.name];
                        var dataRows = dataForExport[dataKey][dataIndex].slice(0, -1); // Exclude totals row
                        dataRows = dataRows.map(row => { return formatMetric(tableMetric, row, dataForExport.currency) });

                        tableData.push(...dataRows);
                    } else {
                        tableData.push(formatMetric(tableMetric, flattenedData.find((row) => row[0] === tableMetric.name), dataForExport.currency));
                    }
                }
                mergedData.push({ id: "widget_" + crypto.randomUUID(), data_type: metric.type === "Table (Projections)" ? 'More Than Two Col Table' : 'Chart (Projections)', data: tableData, name: metric.user_given_name });
                continue;
            }

            if (metric.type === "Product Analysis (KPIs)") {
                console.log("Data for Product Analysis (KPIs):");
                let tableData = [
                    [], // Headers
                    [] // Values
                ];
                for (let tableMetric of metric.table_metrics) {
                    tableData[0].push(tableMetric.options["Product Name"].value + ": " + tableMetric.name);
                    let product = dataForExport.productAnalyses.find((p) => p.name === tableMetric.options["Product Name"].value);
                    if (product === undefined) {
                        messageApi.error("Failed to Merge Data, No product with name: " + tableMetric.options["Product Name"].value);
                        return;
                    }
                    let value = product.data[1][product.data[0].indexOf(tableMetric.name)];

                    tableData[1].push(formatMetric(tableMetric, value, dataForExport.currency));
                }
                mergedData.push({ id: "widget_" + crypto.randomUUID(), data_type: 'Two Col Table', data: tableData, name: metric.user_given_name });
                continue;
            }

            if (metric.type === "Pie Chart") {
                if (metric.name === "Cap Table %") {
                    console.log(dataForExport.cap_table_data);
                    var chart_data = dataForExport.cap_table_data.map((item) => {
                        return { label: item.name, value: parseFloat(item.sharePercentage.substring(0, item.sharePercentage.length - 1)) / 100 };
                    });
                    chart_data = chart_data.slice(0, -1); // Remove total entry
                    mergedData.push({ id: "widget_" + crypto.randomUUID(), data_type: 'Pie Chart', data: chart_data, name: metric.user_given_name });
                } else if (metric.name === "Cost Allocation %") {
                    console.log(entitySelected);
                    mergedData.push({
                        id: "widget_" + crypto.randomUUID(),
                        data_type: 'Pie Chart', data: [
                            { label: "GTM and Operational Cost", value: parseFloat(entitySelected.cor_gto_and_operational) },
                            { label: "Infrastructure and Admin Cost", value: parseFloat(entitySelected.cor_infrast_and_admin) },
                            { label: "Product Development Cost", value: parseFloat(entitySelected.cor_product_dev) }
                        ], name: metric.user_given_name
                    });
                }
            }

            if (metric.type === "Table (Pricing)") {
                console.log("Data for Table (Pricing):");
                let tableData = [
                    [], // Headers
                    [] // Values
                ];
                for (let tableMetric of metric.table_metrics) {
                    tableData[0].push(tableMetric.name)
                    tableData[1].push(formatMetric(tableMetric, dataForExport.allPSBBFirmWideMapping[tableMetric.name], dataForExport.currency));
                }
                mergedData.push({ id: "widget_" + crypto.randomUUID(), data_type: 'Two Col Table', data: tableData, name: metric.user_given_name });
                continue;
            }

            if (metric.type === "Miscellaneous") {
                if (metric.options.year) { // Meaning it must be a projection
                    const yearIndex = parseInt(metric.options.year.value);
                    let chart_data = [];

                    for (let item of metric.needed) {
                        const dataRow = flattenedData.find((row) => row[0] === item);
                        chart_data.push({ label: item, value: formatMetric({ 'data_type': metric.needed_data_type }, dataRow[yearIndex + 1], dataForExport.currency) });
                    }

                    mergedData.push({ id: "widget_" + crypto.randomUUID(), data_type: metric.data_type, data: chart_data, name: metric.user_given_name });
                }
            }
        }
        setMergedData(mergedData);
        return mergedData;
    }

    // One updater to change all template data if currentTemplate changes
    const updateCurrentTemplate = (updater) => {
        setCurrentTemplate(prev => {
            const next = typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
            setTemplates(prevTemplates => {
                return prevTemplates.map(template =>
                    template.id === currentTemplate.id ? { ...template, ...next } : template
                );
            });
            return next;
        });
    };

    return (
        <div className="pn-wrap">
            <PageHead eyebrow={entitySelected?.name} title="Export"
                sub={<>Pick or build a template, map your calculated data onto it, then send it to the renderer.</>}>
                <button type="button" className="pn-btn pn-btn-ghost" onClick={() => goTo(3)}><ArrowLeftOutlined /> Back to Dashboard</button>
                <button type="button" className="pn-btn pn-btn-cta" disabled={currentTemplate.id === ""}
                    onClick={() => { const dataToPass = mergeData(); if (dataToPass) { goTo(5); } }}>
                    { currentTemplate.id === "" && <>Select a Template</> }
                    { currentTemplate.id !== "" && <>To Template Render <ArrowRightOutlined /></> }
                </button>
            </PageHead>

            <div className="pn-export-bar">
                <div className="pn-bar-card">
                    <span className="pn-bar-ic"><LayoutOutlined /></span>
                    <span className="pn-bar-lbl">Template</span>
                    <select id="templateSelect" className="pn-control" value={currentTemplate?.id || ""}
                        onChange={(e) => { setCurrentTemplate(templates.find(template => template.id === parseInt(e.target.value))); setTemplateSelected(true); }}>
                        <option value="" disabled hidden>{templates.length === 0 ? "No templates available" : "-- Select a Template --"}</option>
                        {templates.map((template) => (
                            <option key={template.id} value={template.id}>{template.name}</option>
                        ))}
                    </select>
                </div>
                <div className="pn-bar-card">
                    <span className="pn-bar-lbl">New</span>
                    <div className="pn-input-wrap">
                        <input id="newTemplateName" name="newTemplateName" className="pn-control" type="text"
                            placeholder="New template name" value={newTemplateName}
                            onChange={(e) => setNewTemplateName(e.target.value)} />
                    </div>
                    <button className="pn-btn pn-btn-ghost" type="button" onClick={handleAddTemplate}><PlusOutlined /> Add</button>
                </div>
            </div>

            <div className="pn-editor-foot" style={{ marginTop: 16 }}>
                <button className="pn-btn pn-btn-ghost" type="button" onClick={() => setOpenTemplateMaker(!openTemplateMaker)}><SlidersOutlined /> {openTemplateMaker ? 'Hide' : 'Show'} Template Maker</button>
                <div className="sp" />
                <button className="pn-btn pn-btn-accent" type="button" onClick={handleSaveTemplate}><SaveOutlined /> Save template</button>
            </div>

            {openTemplateMaker &&
                <div className="pn-fieldset" style={{ marginTop: 18 }}>
                    <TemplateMaker setMergedData={setMergedData} productName="PSBB" templateSelected={templateSelected} entitySelected={entitySelected} dataToExport={dataForExport} currentTemplate={currentTemplate} setCurrentTemplate={updateCurrentTemplate} />
                </div>
            }
        </div>
    );
}

export default ExportDataPage;
